import mergeClasses from '../../../utils/shallowMerge';
import Button from '../../atoms/button/Button';
import SvgPlus from '../../atoms/icons/Plus';
import InlineMessage from '../../atoms/inlineMessage';
import PrimaryLoader from '../../atoms/primaryLoader/PrimaryLoader';
import { TextField } from '../../atoms/textField/TextField';
import { Text } from '../../typography/text/Text';
import defaultClasses from './PromoCode.module.scss';
import usePromoCode from './usePromoCode';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { InformationStroke } from '../../atoms/icons';

const typePC = {
  default: 'default',
  transform: 'transform',
  accordion: 'accordion',
};

const typeOfMessage = {
  success: 'success',
  error: 'error',
};

const PromoCode = React.forwardRef(
  ({ type, placeholder, placeholderPromo, promoTextInfo, typeMessage, customClasses, showInformMessage }, ref) => {
    const styles = mergeClasses(defaultClasses, customClasses);
    const buttonRef = useRef(null);
    const inputNodeRef = useRef(null);
    const { isClickPromotion, onClickPromotion, setCode, promoState, resetPromoState } = usePromoCode();
    const loading = promoState.loadingAdd;

    const { register, handleSubmit, reset } = useForm({});
    let content = null;

    useEffect(() => {
      resetPromoState();
      inputNodeRef.current && inputNodeRef.current.focus();
    }, [isClickPromotion]);

    const onSubmit = (data) => {
      setCode(data.promocode);
      reset();
    };

    if ([typePC.accordion, typePC.default].includes(type)) {
      const textFieldClassNames = type === typePC.default ? styles.inputDefault : styles.inputAccordion;

      const formClassNames =
        type === typePC.default
          ? clsx(styles.promoCode, styles.formDefault)
          : clsx(styles.promoCode, styles.formAccordion, styles.giftCodeFormAccordion);

      content = (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={formClassNames}>
            <TextField
              className={styles.textField}
              customClasses={{
                input: textFieldClassNames,
              }}
              placeholder={placeholder}
              variant="standard"
              {...register('promocode')}
              error={typeMessage === 'error'}
            />
            <Button
              className={styles.buttonItem}
              disabled={loading}
              ref={buttonRef}
              variant="primaryOutlined"
              submit
              as="button"
            >
              <div className={styles.buttonContent}>
                <span className={(styles.applyButton, styles.promoApplyButton)}>APPLY</span>
                {loading && <PrimaryLoader mode="segment" size={20} />}
              </div>
            </Button>
            {promoState?.message && (
              <InlineMessage className={styles.message} type={promoState?.type} message={promoState?.message} />
            )}
          </div>
          {showInformMessage && (
            <div className={clsx(styles.informMessage, styles.isCartPage)}>
              <InformationStroke />
              <Text as="span" textTransform="none" align="start">
                Gift Cards & Birthday vouchers are redeemed in the Payment Step in Checkout
              </Text>
            </div>
          )}
        </form>
      );
    }

    if (!isClickPromotion && type === typePC.transform) {
      content = (
        <div className={clsx(styles.promoCode, styles.formTransformClosed)} onClick={onClickPromotion}>
          <Text as="span">{promoTextInfo}</Text>
          <SvgPlus className={`${styles.plusItem}`} width="15px" height="15px" />
        </div>
      );
    }

    if (isClickPromotion && type === typePC.transform) {
      content = (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={clsx(styles.promoCode, styles.formTransform)}>
            <TextField
              className={styles.textField}
              customClasses={{
                input: styles.inputTransform,
              }}
              placeholder={placeholderPromo}
              variant="underlined"
              {...register('promocode')}
              autoFocus={true}
              autoComplete="off"
              error={promoState.type === 'error'}
              errorMessage={promoState.type === 'error' ? promoState.message : null}
              successMessage={promoState.type === 'success' ? promoState.message : null}
            />

            <Button
              className={styles.buttonItemTransform}
              variant="primaryOutlined"
              as="text"
              ref={buttonRef}
              submit={true}
            >
              <Text size="xs" as="span" weight="bold">
                {loading ? <PrimaryLoader mode="segment" size={20} /> : <span className={styles.apply}>APPLY</span>}
              </Text>
            </Button>
          </div>

          {showInformMessage && (
            <div className={styles.informMessage}>
              <InformationStroke />
              <Text as="span" textTransform="none" align="start">
                Gift Cards & Birthday vouchers are redeemed in the Payment Step in Checkout
              </Text>
            </div>
          )}
        </form>
      );
    }

    return content;
  },
);

PromoCode.propTypes = {
  setCode: PropTypes.func,
  type: PropTypes.oneOf(Object.keys(typePC)),
  placeholder: PropTypes.string,
  placeholderPromo: PropTypes.string,
  promoTextInfo: PropTypes.string,
  message: PropTypes.string,
  typeMessage: PropTypes.oneOf(Object.keys(typeOfMessage)),
  customClasses: PropTypes.object,
};

PromoCode.defaultProps = {
  placeholder: 'Promotion Code',
  placeholderPromo: 'Promo Code',
  promoTextInfo: 'Apply Promotion Code',
  type: typePC.default,
  message: '',
  typeMessage: typeOfMessage.success,
  customClasses: null,
};

export default PromoCode;
